<template>
  <div
    :title="label"
    :class="[
      {'clear-background' : clearBackground },
      {'emphasized' : (hasValueDisplay() && !disableValueDisplayEmphasis) || emphasize }
    ]" class="base-ifp-filter">
    <i
      v-if="clearable && hasValueDisplay()"
      title="Clear"
      class="ddi ddi-subtract-circle"
      @click.stop="$emit('clear-clicked')"
    />
    <i
      v-if="removable"
      title="Remove"
      class="ddi ddi-remove-circle"
      @click.stop="$emit('close-clicked')"
    />
    <span><i v-if="labelIcon" :class="`ddi-${labelIcon}`" class="ddi" />{{ label }}</span>
    <div v-if="hasValueDisplay()" class="value-display-wrapper">
      <slot name="value-display" />
    </div>
    <template v-if="!optionless">
      <i class="ddi ddi-caret-down" />
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { Comment, defineComponent, Fragment, Text, VNode } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */

/* API */

export default defineComponent({
  name: 'BaseIfpFilter',

  props: {
    label: {
      type: String,
      default: '',
    },
    labelIcon: {
      type: String,
      default: '',
    },
    /* Able to clear value (but not remove the filter) */
    clearable: {
      type: Boolean,
      default: false,
    },
    /* Able to clear/remove in one click */
    removable: {
      type: Boolean,
      default: false,
    },
    optionless: {
      type: Boolean,
      default: false,
    },
    /* By default, filter will emphasize if value display slot has data. 
    You can disable that here and then use the 'emphasize' prop solely to control emphasis */
    disableValueDisplayEmphasis: {
      type: Boolean,
      default: false,
    },
    /* Emphasizes filter */
    emphasize: {
      type: Boolean,
      default: false,
    },
    clearBackground: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['clear-clicked', 'close-clicked'],

  data() {
    return {
      // TO DO
    }
  },

  methods: {
    /* Whether the value-display slot renders any text or element. Called from the template, since a slot can only
       be rendered (and tracked) during render. */
    hasValueDisplay(): boolean {
      const slot = this.$slots['value-display']
      const hasContent = (nodes: VNode[]): boolean =>
        nodes.some(node => {
          if (node.type === Comment) {
            return false
          }
          if (node.type === Text) {
            return typeof node.children === 'string' && node.children.trim().length > 0
          }
          if (node.type === Fragment) {
            return Array.isArray(node.children) && hasContent(node.children as VNode[])
          }
          return true
        })
      return !!slot && hasContent(slot())
    },
  },
})
</script>

<style lang="scss">
.base-ifp-filter {
  display: flex;
  align-items: center;
  border-radius: 20px;
  padding: 0 8px;
  background-color: var(--color-background-header);
  border: $border--dotted-dark;
  height: 28px;
  cursor: pointer;
  max-width: 340px;

  &.clear-background {
    background-color: transparent;
  }

  &.emphasized:not(.clear-background) {
    border-color: $color--blue;
    background-color: var(--color-background-blue-15);
  }

  &:hover {
    background-color: var(--color-background-primary);
  }

  > .ddi-remove-circle,
  > .ddi-subtract-circle {
    cursor: pointer;
    color: var(--color-text-third);
    font-size: 14px;
    margin-right: 4px;

    &:hover {
      color: $color--red;
    }
  }

  > span {
    @include single-line-ellipsis;
    @include lettering--p-sm;
    flex-shrink: 0;
    font-weight: $bold;
    color: var(--color-text-primary);
    max-width: 140px;

    .ddi {
      font-size: 13px;
    }
  }

  > .value-display-wrapper {
    display: flex;

    @include single-line-ellipsis;
    @include lettering--p-sm;
    margin-left: 6px;
    padding-left: 6px;
    border-left: $border--solid-light;
  }

  > .ddi-caret-down {
    margin-left: 4px;
    font-size: 10px;
  }
}
</style>
